import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { useInitializeIncidentsFilters } from './useInitializeIncidentsFilters'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLastData } from '@/hooks/useLastData'
import { useIncidentsBroadCastChannelReceiver } from '@/hooks/useOnNotification'
import {
  PaginationState,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table'
import { useDebounce } from 'use-debounce'

import {
  useIncidentsQuery,
  useIncidentsTableQuery,
} from '@/graphql/generated/hooks'
import { IncidentStatus } from '@/graphql/generated/schemas'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { extendMutedFilter } from '@/redux/incidents/conversion'
import {
  IncidentsView,
  selectIncidentFilterCount,
  selectIncidentsFilterInput,
  selectIncidentsPageSize,
  selectIncidentsView,
  setIncidentQueryFilter,
  setIncidentsView,
} from '@/redux/incidents/incidentsSlice'
import { selectMe, selectPrimaryRole } from '@/redux/me/meSlice'
import { mixpanel, trackEvent } from '@/utils/analytics'

import { DATE_COL_NAME } from '../constants/columnIds'
import { PRIORITY_QUEUE_ANALYTICS } from '../constants/constants'
import { IncidentsListType } from '../types/types'
import { getOrderBy, getPriorityQueueOrderBy } from '../utils/query'

export interface UseIncidentsPageResultIProps {
  incidentList: IncidentsListType[]
  /**
   * Total across all pages, or `undefined` when `noTotalIncidentCount` is on —
   * `incidentsTable` has no count to report. Consumers must treat `undefined`
   * as "unknown", never as zero.
   */
  totalIncidents: number | undefined
  /**
   * Whether a further page exists, or `undefined` when `noTotalIncidentCount`
   * is off — `incidents` leaves `pageInfo.hasNextPage` null, so the page count
   * derived from `totalIncidents` is what drives pagination there.
   */
  hasNextPage: boolean | undefined
  /** True until the first response resolves — drives the count-badge skeleton. */
  hasLoadedOnce: boolean
  /** True while the table should render its loading skeleton (first load only). */
  isTableLoading: boolean
  paginationState: PaginationState
  setPaginationState: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  rowSelection: RowSelectionState
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
  queryFilter: string
  setQueryFilter: Dispatch<SetStateAction<string>>
  isPriorityQueueActive: boolean
  incidentsView: IncidentsView
  onSelectView: (view: IncidentsView) => void
}

/**
 * Owns all Incidents page data/query state so the legacy and rebranded page
 * skins render from one source and can't drift. Extracted from the page in
 * ENG-30677 (whole-page rebrand), mirroring `useGatewaysPage`.
 *
 * Serves the table from either `incidents` or `incidentsTable` depending on
 * `noTotalIncidentCount` (ENG-34177). The two differ only in whether a total
 * count or a `hasNextPage` drives pagination.
 */
export const useIncidentsPage = (): UseIncidentsPageResultIProps => {
  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectIncidentsFilterInput)
  const initialPageSize = useAppSelector(selectIncidentsPageSize)
  const isSalesEnv: boolean = useFeatureFlag(FeatureFlag.salesDatabaseReset)
  const isNoTotalCountEnabled: boolean = useFeatureFlag(
    FeatureFlag.noTotalIncidentCount
  )
  const isIncidentDrawerRebrand: boolean = useFeatureFlag(
    FeatureFlag.incidentDrawerRebrand
  )
  // Only the dark page can render without a total. The legacy page (rebrand
  // off) reads `totalIncidents` for its badge and derives its page count from
  // it, so dropping the count there would blank the badge and compute zero
  // pages. Both flags must be on before the no-count query is used.
  const hasNoTotalCount = isNoTotalCountEnabled && isIncidentDrawerRebrand

  const [queryFilter, setQueryFilter] = useState<string>(null)
  const [debouncedQueryFilter] = useDebounce(queryFilter, 1000)

  // Lifted so changes flow through query variables and hit Apollo's cache
  // instead of bypassing it via refetch().
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const incidentsView = useAppSelector(selectIncidentsView)
  const isPriorityQueueActive = incidentsView === 'priorityQueue'
  const primaryRole = useAppSelector(selectPrimaryRole)
  const activeFilterCount = useAppSelector(selectIncidentFilterCount)
  const me = useAppSelector(selectMe)

  const onSelectView = (nextView: IncidentsView) => {
    const isEnteringPriorityQueue = nextView === 'priorityQueue'
    const isLeavingPriorityQueue =
      isPriorityQueueActive && !isEnteringPriorityQueue

    // Only Priority Queue transitions get this event — switching between All and
    // Acknowledged by Me is tracked by IncidentsViewToggle's own button-press events.
    if (isEnteringPriorityQueue || isLeavingPriorityQueue) {
      trackEvent(PRIORITY_QUEUE_ANALYTICS.toggled, {
        state: isEnteringPriorityQueue ? 'on' : 'off',
        role: primaryRole,
        // Read before dispatch clears extraFilterCriteria.ownerId, or this is short by one.
        active_filters: activeFilterCount,
        previous_sort: sorting?.[0]?.id ?? 'none',
        to_view: nextView,
      })
    }

    dispatch(
      setIncidentsView({
        view: nextView,
        ownerId: nextView === 'acknowledgedByMe' ? me?.id : null,
      })
    )
    setSorting(
      isEnteringPriorityQueue ? [{ id: DATE_COL_NAME, desc: false }] : []
    )
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }))
    setRowSelection({})
  }

  // Reload-while-persisted-active case: local `sorting` always mounts empty, so
  // returning to Priority Queue via persisted state (not a click) needs its own
  // seed, or the Date header shows no arrow while the query still sorts ascending.
  useEffect(() => {
    if (isPriorityQueueActive && sorting.length === 0) {
      setSorting([{ id: DATE_COL_NAME, desc: false }])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPriorityQueueActive])

  const baseFilter = extendMutedFilter(filters)
  const filter = isPriorityQueueActive
    ? { ...baseFilter, statusNotIn: [IncidentStatus.Resolved] }
    : baseFilter

  const queryOptions = {
    fetchPolicy: 'cache-and-network' as const,
    errorPolicy: 'all' as const,
    variables: {
      filter,
      first: paginationState.pageIndex,
      last: paginationState.pageSize,
      orderBy: isPriorityQueueActive
        ? getPriorityQueueOrderBy(sorting?.[0]?.desc)
        : getOrderBy(sorting?.[0]),
    },
    // Hacky, but we want Sales to refresh faster than prod, and decided this after it was already deployed
    // (ie don't have a ton of time to make it super pretty)
    pollInterval: isSalesEnv ? 3_000 : 60_000,
  }

  // Both hooks are declared, but `skip` leaves exactly one mounted. A flag is a
  // build-time constant, so this is decided before either query starts and
  // neither ever begins polling on the skipped path.
  const incidentsResult = useIncidentsQuery({
    ...queryOptions,
    skip: hasNoTotalCount,
  })
  const incidentsTableResult = useIncidentsTableQuery({
    ...queryOptions,
    skip: !hasNoTotalCount,
  })

  const {
    data,
    loading: isLoading,
    refetch,
  } = hasNoTotalCount ? incidentsTableResult : incidentsResult

  // Kept apart rather than merged into a union: the two result types share no
  // discriminant (both fields are optional on `Query`), so a union would need a
  // cast at every read. Only one is ever populated.
  const lastIncidents = useLastData(incidentsResult.data)
  const lastIncidentsTable = useLastData(incidentsTableResult.data)

  useIncidentsBroadCastChannelReceiver(refetch)

  useInitializeIncidentsFilters()

  useEffect(() => {
    dispatch(setIncidentQueryFilter(debouncedQueryFilter || null))
  }, [debouncedQueryFilter])

  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }))
    setRowSelection({})
  }, [JSON.stringify(filters)])

  // Timing must be captured on whichever query is live, or the before/after
  // measurement this flag exists to produce only covers the flag-off half.
  useEffect(() => {
    isLoading
      ? mixpanel.time_event('Page Load: Incidents View')
      : mixpanel.track('Page Load: Incidents View', {
          number_of_incidents: hasNoTotalCount
            ? incidentsTableResult.data?.incidentsTable?.edges?.length
            : incidentsResult.data?.incidents?.edges?.length,
        })
  }, [isLoading])

  // `incidents` has a totalCount and a null pageInfo; `incidentsTable` the
  // reverse. Each stays undefined on the path that cannot supply it, so a
  // consumer can tell "no count" apart from "zero".
  const totalIncidents = hasNoTotalCount
    ? undefined
    : lastIncidents?.incidents?.totalCount ?? 0
  const hasNextPage = hasNoTotalCount
    ? Boolean(lastIncidentsTable?.incidentsTable?.pageInfo?.hasNextPage)
    : undefined

  const lastData = hasNoTotalCount ? lastIncidentsTable : lastIncidents
  const incidentList = hasNoTotalCount
    ? lastIncidentsTable?.incidentsTable?.edges?.map((e) => e.node) ?? []
    : lastIncidents?.incidents?.edges?.map((e) => e.node) ?? []

  return {
    incidentList,
    totalIncidents,
    hasNextPage,
    hasLoadedOnce: Boolean(lastData),
    isTableLoading: isLoading && !data,
    paginationState,
    setPaginationState,
    sorting,
    setSorting,
    rowSelection,
    setRowSelection,
    queryFilter,
    setQueryFilter,
    isPriorityQueueActive,
    incidentsView,
    onSelectView,
  }
}
